import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Header,
  KeyValuePairs,
  SpaceBetween,
  Button,
  Form,
  FormField,
  Input,
  Alert,
  Spinner,
  BreadcrumbGroup,
  ColumnLayout,
  Box,
  StatusIndicator,
  Tabs,
  Icon,
  Badge,
} from "@cloudscape-design/components";
import { useAuth } from "../AuthContext";
import { useNotifications } from "./NotificationContext";
import { AUTH_API } from "../config/api";
import AppLayoutWrapper from "./AppLayoutWrapper";

const Profile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ first_name: "", last_name: "", phone: "" });
  const [retryingOnboarding, setRetryingOnboarding] = useState(false);

  const accessToken = user?.access_token || user?.token;

  const handleRetryOnboarding = async () => {
    if (!accessToken) return;
    setRetryingOnboarding(true);
    try {
      const res = await fetch(AUTH_API.retryOnboarding, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        notifySuccess("Votre espace Adexcloud a été finalisé avec succès.");
        await fetchProfile();
      } else {
        notifyError(data.error || "La finalisation a échoué. Veuillez réessayer.");
      }
    } catch (err) {
      notifyError("Erreur réseau lors de la finalisation.");
    } finally {
      setRetryingOnboarding(false);
    }
  };

  const fetchProfile = useCallback(async () => {
    if (!accessToken) return;
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(AUTH_API.me, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setProfile(data.user);
        setFormData({
          first_name: data.user.first_name || "",
          last_name: data.user.last_name || "",
          phone: data.user.phone || "",
        });
      } else {
        setError(data.error || "Impossible de charger le profil.");
      }
    } catch (err) {
      setError("Erreur réseau lors du chargement du profil.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSave = async () => {
    if (!accessToken) return;
    try {
      setSaving(true);
      const res = await fetch(AUTH_API.profile, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        notifySuccess("Profil mis à jour avec succès.");
        setEditMode(false);
        await fetchProfile();
      } else {
        notifyError(data.message || "Erreur lors de la mise à jour du profil.");
      }
    } catch (err) {
      notifyError("Erreur réseau lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  const pairs = profile
    ? [
        { label: "Username", value: profile.username || "N/A" },
        { label: "Email", value: profile.email || "N/A" },
        { label: "Prénom", value: profile.first_name || "N/A" },
        { label: "Nom", value: profile.last_name || "N/A" },
        { label: "Téléphone", value: profile.phone || "N/A" },
        { label: "Rôle", value: profile.global_role || "user" },
        { label: "Member since", value: profile.date_joined ? new Date(profile.date_joined).toLocaleDateString() : "N/A" },
        { label: "Last login", value: profile.last_login ? new Date(profile.last_login).toLocaleString() : "Never" },
      ]
    : [];

  return (
    <AppLayoutWrapper
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: "Home", href: "/dashboard" },
            { text: "Profile", href: "/profile" },
          ]}
          onFollow={(event) => {
            event.preventDefault();
            navigate(event.detail.href);
          }}
        />
      }
      header={{
        title: "Profile",
        description: "Manage your personal information, account status and security preferences.",
      }}
      headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
    >
      <SpaceBetween size="l">
        {error && <Alert type="error">{error}</Alert>}
        {loading && <Box textAlign="center" padding={{ top: "xxxl" }}><Spinner size="large" /></Box>}
        {!loading && profile && (
          <>
            <Container header={<Header variant="h2" description="Your Adexgénie account overview">Account summary</Header>}>
              <ColumnLayout columns={{ default: 1, s: 4 }} variant="text-grid">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #0f2b4d, #1a5fb4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                    <Icon name="user-profile" variant="inverted" size="big" />
                  </div>
                  <div>
                    <Box variant="h1">{profile.first_name && profile.last_name ? `${profile.first_name} ${profile.last_name}` : profile.username || 'User'}</Box>
                    <Box variant="p" color="text-status-general">{profile.email || 'No email'}</Box>
                    <SpaceBetween direction="horizontal" size="xs" style={{ marginTop: '8px' }}>
                      <Badge color="blue">{profile.global_role || 'user'}</Badge>
                      <StatusIndicator type="success">Account active</StatusIndicator>
                    </SpaceBetween>
                  </div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Member since</Box>
                  <Box variant="p">{profile.date_joined ? new Date(profile.date_joined).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Last login</Box>
                  <Box variant="p">{profile.last_login ? new Date(profile.last_login).toLocaleString() : 'Never'}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Onboarding</Box>
                  {profile.adexcloud_onboarding_pending ? (
                    <StatusIndicator type="warning">Pending</StatusIndicator>
                  ) : (
                    <StatusIndicator type="success">Completed</StatusIndicator>
                  )}
                </div>
              </ColumnLayout>
            </Container>

            {profile?.adexcloud_onboarding_pending && (
              <Alert
                type="warning"
                header="Finalisation du compte en attente"
                action={
                  <Button onClick={handleRetryOnboarding} loading={retryingOnboarding}>
                    Finaliser le compte
                  </Button>
                }
              >
                La configuration de votre espace Adexcloud n&apos;a pas pu être finalisée.
                Cliquez sur le bouton pour relancer la finalisation.
              </Alert>
            )}

            <Tabs
              activeTabId={editMode ? "edit" : "details"}
              onChange={({ detail }) => setEditMode(detail.activeTabId === "edit")}
              tabs={[
                {
                  id: "details",
                  label: "Account details",
                  content: (
                    <Container header={<Header variant="h2" description="Complete account information" actions={<Button onClick={() => setEditMode(true)}>Edit</Button>}>Account details</Header>}>
                      <KeyValuePairs items={pairs} />
                    </Container>
                  ),
                },
                {
                  id: "edit",
                  label: "Edit profile",
                  content: (
                    <Container header={<Header variant="h2" description="Update your personal information">Edit profile</Header>}>
                      <Form
                        actions={
                          <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => setEditMode(false)} disabled={saving}>
                              Cancel
                            </Button>
                            <Button variant="primary" onClick={handleSave} loading={saving}>
                              Save changes
                            </Button>
                          </SpaceBetween>
                        }
                      >
                        <ColumnLayout columns={{ default: 1, s: 2 }}>
                          <FormField label="First name">
                            <Input
                              value={formData.first_name}
                              onChange={(e) => setFormData((p) => ({ ...p, first_name: e.detail.value }))}
                            />
                          </FormField>
                          <FormField label="Last name">
                            <Input
                              value={formData.last_name}
                              onChange={(e) => setFormData((p) => ({ ...p, last_name: e.detail.value }))}
                            />
                          </FormField>
                          <FormField label="Phone">
                            <Input
                              value={formData.phone}
                              onChange={(e) => setFormData((p) => ({ ...p, phone: e.detail.value }))}
                            />
                          </FormField>
                        </ColumnLayout>
                      </Form>
                    </Container>
                  ),
                },
              ]}
            />
          </>
        )}
      </SpaceBetween>
    </AppLayoutWrapper>
  );
};

export default Profile;
